import type { JSX } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import {
  Icon,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui';
import type { IconName } from '@/components/ui/icon';
import { toast } from '@/lib/toast';
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { CHANGELOG_MENU_LABEL } from '@/data/changelog';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useDraftReportsCount } from '@/hooks/useDraftReportsCount';
import { useAuth } from '@/features/auth/AuthProvider';

export interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  path: string;
}

interface AppSidebarProps {
  items: NavItem[];
  activeItem?: string;
  settingsPath: string;
  variant: 'desktop' | 'mobile';
  className?: string;
  onNavigate?: () => void;
  sidebarVariant?: 'sidebar' | 'floating' | 'inset';
}

function SidebarHeaderButton({
  icon,
  label,
  onClick,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-10 w-10 items-center justify-center rounded-md border border-[var(--border-default)] bg-surface text-text-secondary shadow-sm transition-all duration-150',
        'hover:border-brand-blue-500 hover:bg-brand-blue-50 hover:text-brand-blue-500 hover:shadow-md',
        'active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-300/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
      )}
    >
      <Icon name={icon} className="text-lg" aria-hidden />
    </button>
  );
}

function SidebarNavLink({
  item,
  active,
  collapsed,
  onNavigate,
  badge,
}: {
  item: { id: string; label: string; icon: IconName; path: string };
  active: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
  badge?: number;
}) {
  return (
    <Link
      to={item.path}
      preload="intent"
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={cn(
        'group relative flex h-9 items-center gap-3 px-3 py-1.5',
        // Smooth transitions
        'transition-all duration-150 ease-out',
        // Focus states
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-300/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        collapsed ? 'w-9 justify-center rounded-md px-0' : 'justify-start rounded-md',
        active
          ? // Active state - blue background
            'bg-brand-blue-100 text-brand-blue-500 font-semibold'
          : // Hover state
            'text-text-primary hover:bg-brand-blue-50 hover:text-brand-blue-500'
      )}
    >
      <Icon
        name={item.icon}
        className={cn(
          'text-xl transition-transform duration-150',
          active ? 'scale-105' : 'group-hover:scale-102'
        )}
        aria-hidden
      />
      {collapsed ? (
        <span className="sr-only">{item.label}</span>
      ) : (
        <>
          <span
            className={cn(
              'text-sm font-semibold transition-all duration-150',
              active && 'tracking-wide'
            )}
          >
            {item.label}
          </span>
          {badge !== undefined && badge > 0 ? (
            <span className="ml-auto rounded-md bg-brand-blue-100 px-2 py-0.5 text-xs font-semibold text-brand-blue-500 transition-transform duration-150">
              {badge}
            </span>
          ) : null}
        </>
      )}
    </Link>
  );
}

function SidebarUser({
  name,
  email,
  collapsed,
  onLogout,
}: {
  name: string;
  email: string;
  collapsed: boolean;
  onLogout: () => void;
}) {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors',
            'hover:bg-brand-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-300/40',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? `${name} · ${email}` : undefined}
          aria-label="Azioni account"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-blue-100 text-sm font-semibold text-brand-blue-600">
            {initials}
          </div>
          {collapsed ? null : (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{name}</div>
              <div className="truncate text-xs text-text-secondary">{email}</div>
            </div>
          )}
          {collapsed ? null : (
            <div className="ml-auto text-text-tertiary">
              <Icon name="ellipsis_vertical" className="text-base" aria-hidden />
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled className="cursor-default">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-blue-100 text-sm font-semibold text-brand-blue-600">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-text-primary">{name}</div>
              <div className="truncate text-xs text-text-secondary">{email}</div>
            </div>
          </div>
        </DropdownMenuItem>
        <div className="my-1 h-px bg-[var(--border-subtle)]" />
        <DropdownMenuItem onClick={onLogout}>Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppSidebar({
  items,
  activeItem,
  settingsPath,
  variant,
  className,
  onNavigate,
  sidebarVariant = 'sidebar',
}: AppSidebarProps): JSX.Element {
  const { state, setOpenMobile } = useSidebar();
  const { isOnline } = useNetworkStatus();
  const draftReportsCount = useDraftReportsCount();
  const { user, signOut } = useAuth();

  // Removed recent section: now handled on Home screen.

  const isCollapsed = variant === 'desktop' ? state === 'collapsed' : false;
  const isCollapsedDesktop = isCollapsed && variant === 'desktop';
  const primaryItems = items;
  const userEmail = user?.email ?? 'Utente non identificato';
  const userName =
    typeof user?.user_metadata?.full_name === 'string' &&
    user.user_metadata.full_name.trim().length > 0
      ? user.user_metadata.full_name
      : userEmail.includes('@')
        ? userEmail.split('@')[0]
        : 'Ispettore';
  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Logout completato');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Errore durante il logout: ${error.message}`
          : 'Errore durante il logout'
      );
    }
  };

  return (
    <Sidebar
      collapsible={variant === 'desktop' ? 'icon' : 'none'}
      variant={sidebarVariant}
      className={cn(variant === 'mobile' && 'w-full', className)}
    >
      <SidebarHeader
        className={cn(
          'min-h-[72px] items-center',
          isCollapsedDesktop ? 'justify-center' : 'justify-between'
        )}
      >
        <div
          className={cn('flex w-full flex-col items-center gap-2')}
          title={isCollapsedDesktop ? 'Luca Milanesi' : undefined}
        >
          {isCollapsedDesktop ? (
            <img src="/brand/logo-compact.png" alt="Luca Milanesi" className="h-10 w-10" />
          ) : (
            <div className="flex w-full justify-center">
              <img
                src="/brand/pdf-logo.png"
                alt="Luca Milanesi"
                className="h-9 w-auto max-w-[190px]"
              />
            </div>
          )}
        </div>

        {variant === 'mobile' ? (
          <SidebarHeaderButton
            icon="close"
            label="Chiudi menu"
            onClick={() => {
              onNavigate?.();
              setOpenMobile(false);
            }}
          />
        ) : null}
      </SidebarHeader>

      <SidebarContent
        className={cn(
          'flex flex-col gap-1.5 px-3 py-4 overscroll-contain',
          isCollapsed ? 'items-center px-0' : 'px-3'
        )}
      >
        <nav className={cn('flex flex-col gap-0.5', isCollapsed && 'items-center')}>
          {/* Primary Navigation */}
          {primaryItems.map((item, index) => {
            // Group dividers: after Home, after Reports (which is index 2)
            const showDividerAfter = item.id === 'home' || item.id === 'reports';
            const isFirstInGroup =
              index === 0 ||
              primaryItems[index - 1]?.id === 'home' ||
              primaryItems[index - 1]?.id === 'reports';
            return (
              <div key={item.id} className="flex flex-col">
                {isFirstInGroup && index > 0 ? (
                  <div
                    className={cn(
                      'mb-1',
                      !isCollapsed && 'mx-2 border-t border-[var(--border-subtle)]'
                    )}
                  />
                ) : null}
                <SidebarNavLink
                  item={item}
                  active={item.id === activeItem}
                  collapsed={isCollapsed}
                  onNavigate={onNavigate}
                  badge={item.id === 'reports' ? draftReportsCount : undefined}
                />
                {showDividerAfter ? <div className="h-2" /> : null}
              </div>
            );
          })}
        </nav>
      </SidebarContent>

      {!isOnline && !isCollapsed ? (
        <div className="px-3 pb-3 animate-in fade-in duration-200">
          <div className="flex items-center gap-2 rounded-md border border-warning-100/50 bg-warning-100/50 px-3 py-2">
            <Icon name="cloud_off" className="text-base text-warning-500" aria-hidden />
            <span className="text-xs text-warning-500">Offline</span>
          </div>
        </div>
      ) : null}

      <SidebarFooter
        className={cn(
          'flex flex-col gap-1.5 px-3 pb-4',
          isCollapsed ? 'items-center px-0' : 'px-3'
        )}
      >
        <div className="flex flex-col gap-0.5">
          <SidebarNavLink
            item={{
              id: 'guide',
              label: 'Guida',
              icon: 'info',
              path: '/guide',
            }}
            active={activeItem === 'guide'}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
          />

          <SidebarNavLink
            item={{
              id: 'changelog',
              label: CHANGELOG_MENU_LABEL,
              icon: 'history',
              path: '/changelog',
            }}
            active={activeItem === 'changelog'}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
          />

          <SidebarNavLink
            item={{
              id: 'settings',
              label: 'Impostazioni',
              icon: 'settings',
              path: settingsPath,
            }}
            active={activeItem === 'settings'}
            collapsed={isCollapsed}
            onNavigate={onNavigate}
          />
        </div>

        <div className="mt-2">
          <SidebarUser
            name={userName}
            email={userEmail}
            collapsed={isCollapsed}
            onLogout={handleLogout}
          />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
